import React, { useState } from 'react';

import { toast } from 'react-toastify';

import { CompanyRequestIcon } from '#/assets/svgs';
import { FieldError } from '#/common/components';
import { useFormikForm } from '#/common/hooks';
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    Input,
} from '#/components/ui';
import { apiEndpoints, queryKeys, usePostMutation } from '#/services';
import { requestCompanyInitialValues, RequestCompanyValidationSchema } from '#/utils';

const RequestCompanyDialog = () => {
    const [openModal, setOpenModal] = useState(false);

    const { mutate: requestCompany, isLoading: requestCompanyApiLoading } = usePostMutation({
        url: apiEndpoints.COMPANY_REQUEST,
        queryKey: [queryKeys.COMPANIES],
        successMessage: 'Company requested successfully!',
        onSuccess: handleCancelClick,
        onError: error => {
            const errorMessage =
                error?.response?.data?.name?.[0] || error?.response?.data?.detail || 'Failed to request company';
            toast.error(errorMessage);
        },
    });

    function handleModalChange(isOpen) {
        setOpenModal(isOpen);
        resetForm();
    }

    function handleRequestCompany(values) {
        requestCompany({
            payload: values,
        });
    }

    const { values, errors, touched, handleChange, handleSubmit, resetForm } = useFormikForm(
        requestCompanyInitialValues,
        handleRequestCompany,
        RequestCompanyValidationSchema
    );

    function handleCancelClick() {
        setOpenModal(false);
        resetForm();
    }

    return (
        <>
            <Dialog open={openModal} onOpenChange={handleModalChange}>
                <DialogTrigger asChild>
                    <Button>Add to THE LIST</Button>
                </DialogTrigger>

                <DialogContent className='max-w-[420px] bg-white overflow-auto px-4 py-5 gap-0'>
                    <form onSubmit={handleSubmit}>
                        <DialogHeader className='items-start space-y-2'>
                            <div className='flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full'>
                                <CompanyRequestIcon className='w-12 h-12' />
                            </div>
                            <DialogTitle className='pt-3 text-lg font-medium text-gray900'>
                                Request a Company
                            </DialogTitle>
                            <DialogDescription asChild>
                                <div className='text-start text-gray500 text-sm font-normal'>
                                    Can’t find the company you're looking for? Enter its name below and our team will
                                    add it to THE LIST.
                                </div>
                            </DialogDescription>
                        </DialogHeader>

                        <div className='space-y-4 pt-5'>
                            <div className='space-y-2'>
                                <label htmlFor='name' className='block text-sm font-medium text-gray700'>
                                    Company name
                                </label>
                                <Input
                                    id='name'
                                    name='name'
                                    type='text'
                                    placeholder='Enter your company name'
                                    className='w-full px-4 py-3 h-11 border border-borderGray rounded-lg shadow-sm placeholder:text-sm'
                                    value={values.name}
                                    onChange={handleChange}
                                />
                                <FieldError error={touched.name && errors.name} />
                            </div>

                            <div className='flex justify-between gap-2 w-full items-center pt-4'>
                                <Button
                                    type='button'
                                    variant='outline'
                                    onClick={handleCancelClick}
                                    className='w-[188px] h-10 text-gray700 bg-transparent border-borderGray'
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type='submit'
                                    isLoading={requestCompanyApiLoading}
                                    className='w-[188px] h-10 text-white text-xs'
                                >
                                    Submit Request
                                </Button>
                            </div>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
};

export { RequestCompanyDialog };
